import { useEffect, useRef, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import logger from '../utils/logger';

interface PullToRefreshOptions {
  enabled?: boolean;
  threshold?: number; // Distance to pull before triggering refresh
  resistance?: number; // Resistance factor (0-1, higher = more resistance)
  onRefresh: () => Promise<void>;
  refreshIndicatorHeight?: number;
}

/**
 * Hook for implementing pull-to-refresh functionality
 * Returns ref to attach to scrollable container and refresh state
 */
export const usePullToRefresh = (options: PullToRefreshOptions) => {
  const {
    enabled = true,
    threshold = 80,
    resistance = 0.6,
    onRefresh,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    refreshIndicatorHeight = 60
  } = options;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number>(0);
  const isPulling = useRef<boolean>(false);
  const isScrolledToTop = useRef<boolean>(true);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    logger.info('Pull to refresh triggered');

    try {
      await onRefresh();
    } catch (error) {
      logger.error('Pull to refresh error:', error);
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [isRefreshing, onRefresh]);

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const container = containerRef.current;
    
    if (!enabled || !container) {
      return;
    }

    // Check scroll position
    const handleScroll = () => {
      if (container) {
        isScrolledToTop.current = container.scrollTop <= 0;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (isRefreshing) return;

      const touch = e.touches[0];
      
      // COMPLETELY ignore touches in bottom nav area - don't even store position
      const screenHeight = window.innerHeight;
      if (touch.clientY > screenHeight - 120) {
        isPulling.current = false;
        return; // Exit early - don't process this touch at all
      }
      
      startY.current = touch.clientY;
      
      // Only enable pull-to-refresh when:
      // 1. Scrolled to top
      // 2. Not starting from screen edges (reserved for swipe navigation)
      const screenWidth = window.innerWidth;
      const isAtScreenEdge = touch.clientX < 20 || touch.clientX > screenWidth - 20;
      
      isPulling.current = isScrolledToTop.current && !isAtScreenEdge;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || isRefreshing) return;

      const touch = e.touches[0];
      const deltaY = touch.clientY - startY.current;

      // Only trigger for downward pulls
      if (deltaY > 0 && isScrolledToTop.current) {
        // Apply resistance
        const resistedDelta = deltaY * resistance;
        const distance = Math.min(resistedDelta, threshold * 1.5);
        
        setPullDistance(distance);

        // Prevent default scrolling when pulling
        if (distance > 10) {
          e.preventDefault();
        }

        // Add haptic feedback on mobile when threshold is reached
        if (platform !== 'web' && distance >= threshold && pullDistance < threshold) {
          // Haptic feedback would be implemented here with Capacitor Haptics plugin
          logger.debug('Pull threshold reached');
        }
      }
    };

    const handleTouchEnd = () => {
      if (!isPulling.current || isRefreshing) return;

      isPulling.current = false;

      // Check if pulled far enough to trigger refresh
      if (pullDistance >= threshold) {
        handleRefresh();
      } else {
        // Reset pull distance with animation
        setPullDistance(0);
      }
    };

    // Add event listeners
    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Initial scroll check
    handleScroll();

    // Cleanup
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, threshold, resistance, pullDistance, isRefreshing, handleRefresh]);

  return {
    containerRef,
    isRefreshing,
    pullDistance,
    pullProgress: Math.min(pullDistance / threshold, 1)
  };
};

export default usePullToRefresh;